import React, { ReactElement, useMemo, useCallback, useState, useEffect } from "react";
import {
    Drawer,
    DrawerBody,
    DrawerFooter,
    DrawerHeader,
    DrawerHeaderTitle,
} from "src/components/Drawer";
import { QualityHubTaskFormDrawerProps, QualityHubTaskFormDrawerState, QualityHubTaskFormDrawerStateProps } from "./QualityHubTaskFormDrawer.types";
import { QualityHubTaskForm } from "../QualityHubTaskForm";
import { localize } from "src/l10n";
import { UnstyledButton } from "src/ui";
import { SmallBodyBolder } from "src/components/Text";
import { PrimaryButton, DefaultButton } from "@fluentui/react";
import {
    UpdateTaskModelHandler,
    TaskModel,
} from "../types";
import {
    createTaskListItemAsync,
    updateTaskListItemAsync,
    createTaskListGroupAsync,
} from "src/api";
import { SpintrTypes } from "src/typings";
import { useDispatch, useSelector } from "react-redux";
import { taskListItemCreated, taskListItemUpdated, findTaskList } from "src/redux";
import { Conditional } from "src/components/Conditional";
import SpintrLoader from "src/ui/components/Loader";
import { setConfirmPopup } from "src/popups/actions";

function getInitialModel(
    listId: number,
    groupId?: number,
    contentStatus: SpintrTypes.ContentStatus = SpintrTypes.ContentStatus.Published,
): TaskModel {
    return {
        listId,
        title: "",
        description: "",
        guidingQuestion: "",
        sortOrder: 0,
        contentStatus,
        groupName: "",
        groupId: groupId,
        allowOverride: true,
        daysOfWeek: 127,
        tagIds: [],
    };
}

/**
 * Renders the QualityHubTaskFormDrawer component.
 *
 * @param {QualityHubTaskFormDrawerProps} props - The component props
 * @returns {ReactElement | null} The rendered component
 */
function QualityHubTaskFormDrawer({
    itemId,
    listId,
    onDismiss,
    open,
    disableGroupCreation,
    initialGroupId,
    onLocalSave,
    localItem
}: QualityHubTaskFormDrawerProps): ReactElement | null {
    const {
        currentUser,
        isLoading,
        item,
        smallViewMode,
        taskList
    } = useSelector<Spintr.AppState, QualityHubTaskFormDrawerStateProps>(
        (appState) => ({
            currentUser: appState.profile.active,
            isLoading: appState.qualityHub.listsLoading[itemId!],
            item: appState.qualityHub.listsById[listId]?.tasks?.find(
                (item) => item.id === itemId),
            taskList: appState.qualityHub.listsById[listId],
            smallViewMode: appState.ui.isSmallViewMode,
        }),
    );

    const isOwnerOfList = useMemo(() => {
        if (!currentUser || !taskList) {
            return false;
        }

        const ownerIds = [
            currentUser.id,
            currentUser.department?.id,
            currentUser.department?.office?.id,
            currentUser.department?.office?.company?.id,
        ].filter((id): id is number => !!id);

        return ownerIds.some(
            (ownerId) => (taskList.targets ?? [])
                .some((target) => target.id === ownerId));
    }, [taskList?.targets, currentUser]);

    const isLeader = currentUser?.leadsDepartment as boolean | undefined;

    const [model, setModel] = useState<TaskModel>(getInitialModel(
        listId,
        undefined,
        !isOwnerOfList
            ? SpintrTypes.ContentStatus.Draft
            : SpintrTypes.ContentStatus.Published,
    ));

    const [state, setState] = useState<QualityHubTaskFormDrawerState>({
        isDirty: false,
        isSaving: false,
    });

    const dispatch = useDispatch();
    const isNew = !itemId;
    const isActionForm = taskList?.listType === SpintrTypes.TaskListType.ActionList;
    const { isSaving } = state;

    const l10n = useMemo(() => ({
        createNew: localize("ADD_TASK"),
        edit: localize("Redigera"),
        cancel: localize("Avbryt"),
        save: localize("Spara"),
        saveAndContinue: localize("SparaOchFortsatt"),
    }), []);

    const onModelChanging = useCallback<UpdateTaskModelHandler>(
        (updater) => {
            setModel((model) => updater(model));
            setState((state) => ({ ...state, isDirty: true }));
        }, [setModel]);

    const onDismissing = useCallback(() => {
        if (!state.isDirty) {
            setModel(getInitialModel(
                listId,
                initialGroupId,
                !isOwnerOfList ? SpintrTypes.ContentStatus.Draft : SpintrTypes.ContentStatus.Published,
            ));
            onDismiss(false);
            return;
        }

        dispatch(setConfirmPopup({
            isOpen: true,
            message: localize("UnsavedChangesWarning"),
            onConfirm: () => {
                setModel(getInitialModel(
                    listId,
                    initialGroupId,
                    !isOwnerOfList ? SpintrTypes.ContentStatus.Draft : SpintrTypes.ContentStatus.Published,
                ));
                setState((state) => ({ ...state, isDirty: false }));
                onDismiss(false);
            },
        }));
    }, [state.isDirty, onDismiss, setModel, setState, listId, initialGroupId]);

    const onCancelClicked = useCallback(onDismissing, [onDismissing]);

    const onSubmitting = useCallback(async (closeOnSuccess?: boolean) => {
        if (isSaving) {
            return;
        }

        setState((state) => ({ ...state, isSaving: true }));

        let item: Spintr.TaskListTask | undefined;
        try {
            let modelToSend = {
                ...model,
                files: model.files?.map((upload) => upload.file)
            };

            if (isActionForm) {
                modelToSend = {
                    ...modelToSend,
                    groupId: undefined,
                    listId,
                };
            } else if (onLocalSave) {
                onLocalSave(modelToSend);
            } else if (!modelToSend.groupId) {
                if (modelToSend.groupName.length < 2) {
                    setState((state) => ({ ...state, isSaving: false }));
                    return;
                }

                try {
                    const listGroup = await createTaskListGroupAsync({
                        listId,
                        name: modelToSend.groupName,
                        sortOrder: 0,
                    });

                    modelToSend = {
                        ...modelToSend,
                        groupId: listGroup.id,
                    };
                } catch (err) {
                    console.error(err);
                    setState((state) => ({ ...state, isSaving: false }));
                    return;
                }
            }

            if (!onLocalSave) {
                item = await (!itemId
                    ? createTaskListItemAsync(modelToSend)
                    : updateTaskListItemAsync(
                        itemId,
                        modelToSend,
                    ));
            }
        } catch (err) {
            setState((state) => ({ ...state, isSaving: false }));
            return;
        }

        if (!onLocalSave) {
            dispatch(!itemId
                ? taskListItemCreated(item)
                : taskListItemUpdated(item));
        }

        setState((state) => ({ ...state, isSaving: false }));
        setModel(getInitialModel(
            listId,
            initialGroupId,
            !isOwnerOfList ? SpintrTypes.ContentStatus.Draft : SpintrTypes.ContentStatus.Published,
        ));

        if (!closeOnSuccess) {
            return;
        }

        onDismiss(true, item);
    }, [itemId, isSaving, model, onDismiss, setState, listId, initialGroupId]);

    const submitAndCloseOnSuccess = useCallback(
        () => onSubmitting(true),
        [onSubmitting]);

    const submitAndKeepOpen = useCallback(
        () => onSubmitting(false),
        [onSubmitting]);

    useEffect(() => {
        if (!itemId) {
            setModel(getInitialModel(
                listId,
                initialGroupId,
                !isOwnerOfList ? SpintrTypes.ContentStatus.Draft : SpintrTypes.ContentStatus.Published,
            ));
            return;
        }

        if (!taskList) {
            dispatch(findTaskList(listId));
            return;
        }

        const tempItem = localItem ?? item;

        if (!tempItem) {
            return;
        }

        setModel({
            ...tempItem,
            groupName: "",
            tagIds: tempItem.tags?.map((tag) => tag.id) ?? [],
        });
    }, [itemId, item, listId, initialGroupId, localItem]);

    const onOpenChanging = useCallback((open?: boolean) => {
        if (open) {
            return;
        }

        onDismissing();
    }, [onDismissing]);

    const titleText = isNew
        ? l10n.createNew
        : (l10n.edit + " \"" + (item?.title ?? model.title) + "\"");

    return (
        <Drawer className="QualityHubTaskFormDrawer"
                onOpenChange={onOpenChanging}
                open={open}
                position="end"
                size={smallViewMode ? "full" : "medium"}>
            <DrawerHeader>
                <DrawerHeaderTitle>
                    {titleText}
                </DrawerHeaderTitle>
            </DrawerHeader>
            <DrawerBody className="QualityHubTaskFormDrawer-body">
                <Conditional condition={isSaving || isLoading}>
                    <SpintrLoader />
                </Conditional>
                <Conditional condition={!isSaving && !isLoading}>
                    <QualityHubTaskForm isActionForm={isActionForm}
                                        isCustomizationForm={!!onLocalSave}
                                        isNew={isNew}
                                        canEditContentStatus={isLeader || !isOwnerOfList}
                                        model={model}
                                        onModelChange={onModelChanging}
                                        disableGroupCreation={disableGroupCreation} />
                </Conditional>
            </DrawerBody>
            <DrawerFooter className="QualityHubTaskFormDrawer-footer">
                <UnstyledButton disabled={isSaving}
                                onClick={onCancelClicked}>
                    <SmallBodyBolder color="contentLight">
                        {l10n.cancel}
                    </SmallBodyBolder>
                </UnstyledButton>
                <Conditional condition={isNew && !onLocalSave}>
                    <DefaultButton className="QualityHubTaskFormDrawer-saveAndClose"
                                   disabled={isSaving}
                                   onClick={submitAndCloseOnSuccess}
                                   type="submit">
                        {l10n.save}
                    </DefaultButton>
                </Conditional>
                <PrimaryButton className="QualityHubTaskFormDrawer-save"
                               disabled={isSaving}
                               onClick={(isNew && !onLocalSave)
                                    ? submitAndKeepOpen 
                                    : submitAndCloseOnSuccess}
                               type="submit">
                    {(isNew && !onLocalSave) ? l10n.saveAndContinue : l10n.save}
                </PrimaryButton>
            </DrawerFooter>
        </Drawer>
    );
}

export default QualityHubTaskFormDrawer;

